import React, { useRef } from "react";
import { Clock3, Loader2, Send, Sparkles } from "lucide-react";
import { Button } from "../ui/button";
import { useTablesAIQuery } from "./TablesAIQueryContext";

function TableAIQueryPanel() {
  const textareaRef = useRef(null);
  const { draftQuery, setDraftQuery, entries, displayedEntryId, displayEntry, runQuery } = useTablesAIQuery();

  const exampleQueries = [
    "Quels sont les 10 derniers produits ajoutes ?",
    "Combien de secteurs avons-nous par client ?",
    "Combien de points d'intervention par secteur en moyenne par client ?",
  ];

  const handleSubmit = async () => {
    if (!draftQuery.trim()) {
      return;
    }
    try {
      await runQuery(draftQuery);
    } catch {}
  };

  return (
    <div className="flex h-full flex-col">
      <div className="border-b border-gray-200 px-5 py-4 dark:border-gray-800">
        <div className="flex items-start gap-3">
          <div className="rounded-xl bg-purple-100 p-3 text-purple-600 dark:bg-purple-950/50 dark:text-purple-300">
            <Sparkles className="h-5 w-5" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">AI Query Assistant</h3>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              Describe the information you need, the assistant will generate and execute the SQL query.
            </p>
          </div>
        </div>
      </div>

      <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto px-5 py-5">
        <div className="space-y-3">
          <textarea
            ref={textareaRef}
            value={draftQuery}
            onChange={(event) => setDraftQuery(event.target.value)}
            onKeyDown={(event) => {
              if ((event.metaKey || event.ctrlKey) && event.key === "Enter") {
                event.preventDefault();
                handleSubmit();
              }
            }}
            placeholder="Ex: Show me the latest brands created for each client"
            rows={4}
            className="w-full rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm text-gray-900 outline-none transition focus:border-blue-500 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100"
          />
          <div className="flex flex-wrap gap-2">
            {exampleQueries.map((example) => (
              <button
                key={example}
                onClick={() => {
                  setDraftQuery(example);
                  textareaRef.current?.focus();
                }}
                className="rounded-full bg-gray-100 px-3 py-1 text-xs text-gray-700 transition hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700"
              >
                {example}
              </button>
            ))}
          </div>
          <div className="flex items-center justify-between gap-3">
            <div className="text-xs text-gray-500 dark:text-gray-400">Cmd/Ctrl + Enter to run</div>
            <Button
              onClick={handleSubmit}
              disabled={!draftQuery.trim() || entries.some((entry) => entry.status === "loading")}
            >
              {entries.some((entry) => entry.status === "loading") ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Send className="mr-2 h-4 w-4" />
              )}
              {entries.some((entry) => entry.status === "loading") ? "Running..." : "Run query"}
            </Button>
          </div>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto">
          <div className="mb-3 flex items-center justify-between">
            <div className="text-xs font-semibold uppercase tracking-[0.2em] text-gray-500 dark:text-gray-400">
              History
            </div>
            <div className="text-xs text-gray-400">{entries.length}/30</div>
          </div>
          <div className="space-y-2">
            {entries.map((entry) => {
              const isActive = entry.id === displayedEntryId;
              return (
                <button
                  key={entry.id}
                  onClick={() => displayEntry(entry.id)}
                  className={`w-full rounded-xl border px-3 py-3 text-left transition ${
                    isActive
                      ? "border-purple-500 bg-purple-50 dark:border-purple-500 dark:bg-purple-950/40"
                      : "border-gray-200 bg-white hover:bg-gray-50 dark:border-gray-800 dark:bg-gray-900 dark:hover:bg-gray-950"
                  }`}
                >
                  <div className="line-clamp-2 text-sm font-medium text-gray-900 dark:text-gray-100">{entry.query}</div>
                  <div className="mt-2 flex items-center justify-between gap-2 text-xs text-gray-500 dark:text-gray-400">
                    <div className="flex items-center gap-2">
                      <span
                        className={`rounded-full px-2 py-0.5 ${
                          entry.status === "success"
                            ? "bg-green-100 text-green-700 dark:bg-green-950/60 dark:text-green-300"
                            : entry.status === "error"
                              ? "bg-red-100 text-red-700 dark:bg-red-950/60 dark:text-red-300"
                              : "bg-purple-100 text-purple-700 dark:bg-purple-950/60 dark:text-purple-300"
                        }`}
                      >
                        {entry.status}
                      </span>
                      {entry.response?.row_count !== undefined && <span>{entry.response.row_count} rows</span>}
                    </div>
                    <div className="flex items-center gap-1">
                      <Clock3 className="h-3.5 w-3.5" />
                      <span>{new Date(entry.createdAt).toLocaleTimeString()}</span>
                    </div>
                  </div>
                  {entry.status === "error" && (
                    <div className="mt-2 line-clamp-2 text-xs text-red-600 dark:text-red-300">{entry.error}</div>
                  )}
                </button>
              );
            })}
            {entries.length === 0 && (
              <div className="rounded-xl border border-dashed border-gray-200 px-4 py-8 text-center text-sm text-gray-500 dark:border-gray-800 dark:text-gray-400">
                No query yet.
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default TableAIQueryPanel;
